/**
 * Parses the semicolon-separated "Array" settings-variable input into typed
 * elements. Extracted unchanged from the four near-identical onChange*Array
 * handlers in TemplateForm / HandleRequired / HandleOptional (SCRUM-550);
 * the grammar (and its quirks) is pinned by the characterization tests from
 * SCRUM-503/630.
 *
 * Types: Object/Float/Int/Boolean/Array — everything else is kept as the raw
 * string, exactly as typed. Elements that look like a JSON object/array but
 * fail validation are dropped from the result and reported in `errors`
 * instead of being logged, so callers (and later SCRUM-504) can surface them.
 *
 * @param raw the raw input value, elements separated by ';'
 */
export const parseArrayValue = (
  raw: string
): { values: any[]; errors: { element: string; kind: "json" | "array" }[] } => {
  const values: any[] = [];
  const errors: { element: string; kind: "json" | "array" }[] = [];
  const Obj = /^\{[0-9a-zA-Z:",[\]{} ]*\}$/;
  const numInt = /^\d+$/;
  const isValidJson = (x: string): boolean =>
    /^[\],:{}\s]*$/.test(
      x
        .replace(/\\["\\/bfnrtu]/g, "@")
        .replace(
          /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g,
          "]"
        )
        .replace(/(?:^|:|,)(?:\s*\[)+/g, "")
    );

  raw.split(";").map((x) => {
    if (Obj.test(x)) {
      if (isValidJson(x)) {
        try {
          values.push(JSON.parse(x));
        } catch {
          // isValidJson is a regex heuristic; JSON.parse is authoritative. A
          // shape-valid but still-malformed element (e.g. a truncated object)
          // must be reported, not thrown — an unguarded parse here crashed the
          // onBlur handler (SCRUM-633).
          errors.push({ element: x, kind: "json" });
        }
      } else {
        errors.push({ element: x, kind: "json" });
      }
      // Case Float
    } else if (!isNaN(Number(x)) && x.toString().indexOf(".") !== -1) {
      values.push(parseFloat(x));
    }
    // Case Int
    else if (numInt.test(x)) {
      // parseInt without radix: pre-existing quirk, pinned in the unit tests
      values.push(parseInt(x));
    }
    //Case boolean => case true
    else if (x === "true") {
      values.push(true);
    }
    // Case Boolean => case false
    else if (x === "false") {
      values.push(false);
    }
    //Case Array
    else if (x.startsWith("[")) {
      if (isValidJson(x)) {
        try {
          values.push(JSON.parse(x));
        } catch {
          // Same guard as the object branch: a truncated array like "[1,"
          // passes the shape heuristic but fails JSON.parse (SCRUM-633).
          errors.push({ element: x, kind: "array" });
        }
      } else {
        errors.push({ element: x, kind: "array" });
      }
    }
    //Case String
    else {
      values.push(x);
    }
  });

  return { values, errors };
};
